import { ChangeEvent, useEffect, useState } from "react";
import { apiClient } from "src/dm/utils/axios";
import { APIS, IMPERSONATION_STORAGE_KEY } from "src/dm/utils/constants";

// ---------------------------------------------------------------------------
// Dev-only impersonation switcher
// ---------------------------------------------------------------------------
// Renders a dropdown of mock WEBSEC2 users (loaded from the API's dev-only
// /users/dev/available-impersonations endpoint). Selecting a user writes the
// id to localStorage[dm-impersonate]; the axios interceptor in
// src/utils/axios.ts injects it as X-Impersonate-User on every subsequent
// request. After a change we hard-refresh so cached React Query state
// (useCurrentUser, role-gated UI) reloads against the new identity.
//
// Hidden in production via `import.meta.env.MODE`.
// ---------------------------------------------------------------------------

interface ImpersonationUser {
  userId: string;
  fullName: string;
  privileges: string[];
  categoryManagerScope: number[];
  vendorScope: string[];
  roleSummary: string;
}

interface AvailableImpersonationsResponse {
  impersonationHeader: string;
  users: ImpersonationUser[];
}

const isDev = import.meta.env.MODE === "development";

const readSaved = (): string => {
  try {
    return window.localStorage.getItem(IMPERSONATION_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
};

const ImpersonationSwitcher = () => {
  const [users, setUsers] = useState<ImpersonationUser[]>([]);
  const [selected, setSelected] = useState<string>(readSaved);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!isDev) return;
    let cancelled = false;
    apiClient
      .get<AvailableImpersonationsResponse>(APIS.DevImpersonations)
      .then((res) => {
        if (cancelled) return;
        setUsers(res.data.users ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        // 404 outside Development is expected — only surface other errors.
        if (err?.response?.status !== 404) {
          setLoadError(err?.message ?? "Failed to load impersonations");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!isDev) return null;

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelected(value);
    try {
      if (value) {
        window.localStorage.setItem(IMPERSONATION_STORAGE_KEY, value);
      } else {
        window.localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
      }
    } catch {
      // Storage disabled — silent no-op.
    }
    // Hard reload so React Query caches drop and every gate re-evaluates.
    window.location.reload();
  };

  const currentLabel = users.find((u) => u.userId === selected)?.roleSummary;

  return (
    <div
      data-testid="dev-impersonation-switcher"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        fontSize: "0.8rem",
        color: "#cbd5e1",
      }}
      title={loadError ?? currentLabel ?? "Dev impersonation"}
    >
      <span style={{ opacity: 0.7 }}>impersonate:</span>
      <select
        value={selected}
        onChange={handleChange}
        style={{
          background: "#0a3d62",
          color: "#fff",
          border: "1px solid #1d5a86",
          borderRadius: 4,
          padding: "0.15rem 0.35rem",
          fontSize: "0.8rem",
          maxWidth: 220,
        }}
      >
        <option value="">(real identity)</option>
        {users.map((u) => (
          <option key={u.userId} value={u.userId} title={u.roleSummary}>
            {u.userId} — {u.fullName}
          </option>
        ))}
      </select>
    </div>
  );
};

export default ImpersonationSwitcher;
